import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { formatCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, ShoppingCart, Receipt, Landmark, ArrowDownCircle,
  ArrowUpCircle, Package, Users, FolderKanban, Target,
} from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, cls = "text-primary", testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-xl bg-muted ${cls}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold font-mono truncate">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default function BIPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/bi/executive`, { headers, withCredentials: true });
        setData(res.data);
      } catch { toast.error("Erro ao carregar BI"); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data) return null;

  const growthPositive = (data.growth_pct || 0) >= 0;

  return (
    <div className="space-y-6" data-testid="bi-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">BI — Dashboard Executivo</h1>
        <p className="text-muted-foreground">Visão geral do negócio em tempo real</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={growthPositive ? TrendingUp : TrendingDown} label="Vendas do Mês" value={formatCurrency(data.sales_this_month)}
          sub={data.growth_pct != null ? `${growthPositive ? "+" : ""}${data.growth_pct}% vs mês anterior` : `Mês anterior: ${formatCurrency(data.sales_last_month)}`}
          cls={growthPositive ? "text-emerald-600" : "text-red-500"} testid="bi-sales" />
        <Kpi icon={ShoppingCart} label="Pedidos do Mês" value={data.orders_this_month} sub={`Ticket médio: ${formatCurrency(data.avg_ticket)}`} testid="bi-orders" />
        <Kpi icon={Landmark} label="Saldo em Bancos" value={formatCurrency(data.bank_balance)} cls="text-blue-500" testid="bi-bank" />
        <Kpi icon={Package} label="Valor do Stock" value={formatCurrency(data.stock_value)} cls="text-amber-600" testid="bi-stock" />
        <Kpi icon={ArrowDownCircle} label="Contas a Receber" value={formatCurrency(data.receivables)} cls="text-emerald-600" testid="bi-receivables" />
        <Kpi icon={ArrowUpCircle} label="Contas a Pagar" value={formatCurrency(data.payables)} cls="text-red-500" testid="bi-payables" />
        <Kpi icon={Users} label="Funcionários" value={data.headcount} testid="bi-headcount" />
        <Kpi icon={FolderKanban} label="Projetos Ativos" value={data.active_projects} testid="bi-projects" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <p className="font-semibold mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-primary" /> Top 5 Produtos do Mês</p>
            {data.top_products.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Sem vendas este mês</p>
            ) : (
              <div className="space-y-2">
                {data.top_products.map((p, i) => (
                  <div key={i} className="flex justify-between items-center text-sm border-b pb-1">
                    <span><span className="text-muted-foreground font-mono mr-2">{i + 1}.</span>{p.name}</span>
                    <span className="font-mono font-medium">{formatCurrency(p.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="font-semibold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> Pipeline Comercial (CRM)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Oportunidades Abertas</p>
                <p className="text-2xl font-bold">{data.open_opportunities}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Valor do Pipeline</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(data.pipeline_value)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
